import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { DEG, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { PointCloud3dValues } from './params';

/**
 * The text as a point cloud in space.
 *
 * Deliberately projected by hand rather than through three.js. The cloud is a flat list
 * of coloured points with no lighting, no geometry and no materials — a perspective
 * divide and a painter's-algorithm sort is the entire renderer, and doing it directly
 * avoids a second WebGL context and its memory for something a 2D canvas draws fine.
 *
 * Points are sorted back-to-front once per parameter change; without that the cloud
 * looks flat because near and far points overlap in arbitrary order.
 */
export interface PointCloud3dState {
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  tint: Float32Array;
  /** Draw order, back to front. */
  order: Uint32Array;
  count: number;
  palette: ResolvedPalette;
  minZ: number;
  maxZ: number;
  built: boolean;
}

function values(ctx: TechniqueContext): PointCloud3dValues {
  return ctx.values as unknown as PointCloud3dValues;
}

export function init(ctx: TechniqueContext): PointCloud3dState {
  const v = values(ctx);
  const samples = ctx.field.samplePoints(v.points, 'uniform', ctx.rng);
  const count = samples.length / 2;

  const state: PointCloud3dState = {
    x: new Float32Array(count),
    y: new Float32Array(count),
    z: new Float32Array(count),
    tint: new Float32Array(count),
    order: new Uint32Array(count),
    count,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    minZ: 0,
    maxZ: 1,
    built: false,
  };

  const bounds = ctx.field.bounds;
  const centreX = bounds.x + bounds.width / 2;
  const centreY = bounds.y + bounds.height / 2;
  const noise: NoiseFunction2D = createNoise2D(() => ctx.rng.next());

  let minZ = Infinity;
  let maxZ = -Infinity;

  for (let i = 0; i < count; i++) {
    let px = samples[i * 2] - centreX;
    let py = samples[i * 2 + 1] - centreY;
    let pz = ctx.rng.signed(v.depthSpread / 2);

    if (v.noiseDisplacement > 0) {
      pz += noise(samples[i * 2] * v.noiseScale, samples[i * 2 + 1] * v.noiseScale) * v.noiseDisplacement;
    }

    if (v.explosion > 0) {
      // Uniform direction on the sphere, so the burst has no axis bias.
      const u = ctx.rng.next() * 2 - 1;
      const theta = ctx.rng.next() * Math.PI * 2;
      const r = Math.cbrt(ctx.rng.next()) * v.explosion;
      const s = Math.sqrt(1 - u * u);
      px += Math.cos(theta) * s * r;
      py += Math.sin(theta) * s * r;
      pz += u * r;
    }

    state.x[i] = px;
    state.y[i] = py;
    state.z[i] = pz;
    state.tint[i] = ctx.rng.next();
    if (pz < minZ) minZ = pz;
    if (pz > maxZ) maxZ = pz;
  }

  state.minZ = minZ === Infinity ? 0 : minZ;
  state.maxZ = maxZ === -Infinity ? 1 : maxZ;
  return state;
}

export function step(state: PointCloud3dState, _dt: number, ctx: TechniqueContext): PointCloud3dState {
  if (state.built) return state;
  const v = values(ctx);

  // Camera basis.
  const az = v.azimuth * DEG;
  const el = v.elevation * DEG;
  const cosA = Math.cos(az);
  const sinA = Math.sin(az);
  const cosE = Math.cos(el);
  const sinE = Math.sin(el);

  // View-space depth per point, used only for sorting here.
  const depth = new Float32Array(state.count);
  for (let i = 0; i < state.count; i++) {
    const x = state.x[i];
    const y = state.y[i];
    const z = state.z[i];
    const rx = x * cosA - z * sinA;
    const rz = x * sinA + z * cosA;
    depth[i] = -(y * sinE + rz * cosE) + rx * 0;
  }

  const order = Array.from({ length: state.count }, (_, i) => i);
  order.sort((a, b) => depth[a] - depth[b]);
  state.order.set(order);
  state.built = true;
  return state;
}

export function isComplete(state: PointCloud3dState): boolean {
  return state.built;
}

export function draw(state: PointCloud3dState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const bounds = ctx.field.bounds;

  const az = v.azimuth * DEG;
  const el = v.elevation * DEG;
  const cosA = Math.cos(az);
  const sinA = Math.sin(az);
  const cosE = Math.cos(el);
  const sinE = Math.sin(el);

  const extent = Math.max(1, Math.hypot(bounds.width, bounds.height) / 2);
  const camDistance = (extent / Math.tan((v.fov * DEG) / 2)) * v.distance;
  const focal = target.height / (2 * Math.tan((v.fov * DEG) / 2));

  const cx = target.width / 2;
  const cy = target.height / 2;
  const zRange = Math.max(1, state.maxZ - state.minZ);

  // Bucket by colour: hundreds of thousands of points cannot each set a fill style.
  const BUCKETS = 40;
  const paths: Path2D[] = Array.from({ length: BUCKETS }, () => new Path2D());
  const used = new Uint8Array(BUCKETS);

  for (let k = 0; k < state.count; k++) {
    const i = state.order[k];
    const x = state.x[i];
    const y = state.y[i];
    const z = state.z[i];

    // Rotate around Y then X, then perspective divide.
    const rx = x * cosA - z * sinA;
    const rz0 = x * sinA + z * cosA;
    const ry = y * cosE - rz0 * sinE;
    const rz = y * sinE + rz0 * cosE;

    const viewZ = rz + camDistance;
    if (viewZ < 1) continue;

    const scale = focal / viewZ;
    const sx = cx + rx * scale;
    const sy = cy + ry * scale;
    if (sx < -20 || sy < -20 || sx > target.width + 20 || sy > target.height + 20) continue;

    // Depth of field as a fade rather than a blur: cheap, and at point scale a blurred
    // dot and a faded dot are indistinguishable.
    let alpha = v.opacity;
    if (v.depthOfField > 0) {
      const focusError = Math.abs(viewZ - camDistance) / Math.max(1, extent);
      alpha *= 1 - clamp01(focusError * v.depthOfField * 1.6);
      if (alpha < 0.01) continue;
    }

    let t: number;
    switch (v.colorMode) {
      case 'depth':
        t = clamp01((z - state.minZ) / zRange);
        break;
      case 'position':
        t = clamp01((x + bounds.width / 2) / Math.max(1, bounds.width));
        break;
      case 'random':
        t = state.tint[i];
        break;
      default:
        t = 0;
    }

    // Fold alpha into the colour bucket so the fade survives bucketing.
    const bucket = Math.min(BUCKETS - 1, Math.floor(t * (BUCKETS / 2)) * 2 + (alpha > v.opacity * 0.55 ? 1 : 0));
    const radius = Math.max(0.25, (v.pointSize * scale * target.height) / 900);
    paths[bucket].rect(sx - radius, sy - radius, radius * 2, radius * 2);
    used[bucket] = 1;
  }

  for (let b = 0; b < BUCKETS; b++) {
    if (!used[b]) continue;
    const t = Math.floor(b / 2) / Math.max(1, BUCKETS / 2 - 1);
    const alpha = v.opacity * (b % 2 === 1 ? 1 : 0.4);
    c.fillStyle = v.colorMode === 'solid' ? state.palette.stop(0, alpha) : state.palette.at(t, alpha);
    c.fill(paths[b]);
  }
}

export function elementCount(state: PointCloud3dState): number {
  return state.count;
}
